import { apiInstance } from "api/config/axios";
import { IApiResponse } from "api/models/IApiResponse";
import { IGetNodeForLessonProgressRequest } from "api/models/request/IGetNodesForLessonProgressRequest";
import { IGetNodesForLessonProgressResponse } from "api/models/response/IGetNodesForLessonProgressResponse";
import { getApiResponseData } from "api/services/api-service";
import { FileFlat } from "models/FileFlat";
import { IGetLatestNodesConfigurationRequest } from "api/models/request/IGetLatestNodesConfigurationRequest";
import { IAddNodeFileRequest } from "api/models/request/IAddNodeFileRequest";
import { IUpdateLessonNodesRequest } from "api/models/request/IUpdateLessonNodesRequest";
import { IDeleteNodeFileRequest } from "api/models/request/IDeleteNodeFileRequest";
import { IGetNodeFileListRequest } from "api/models/request/IGetNodeFileListRequest";
import { IGetNodeFileGroupsRequest } from "api/models/request/IGetNodeFileGroupsRequest";
import { NodeFileType } from "models/NodeFileType";

const nodeApiBaseUrl = '/node';

export const getNodesForLessonProgress = (request: IGetNodeForLessonProgressRequest & { workspaceId: string }): Promise<IApiResponse<IGetNodesForLessonProgressResponse>> => {
    const url = request.userId ? `${nodeApiBaseUrl}/workspaces/${request.workspaceId}/node-info?lessonProgressId=${request.lessonProgressId}&userId=${request.userId}` : `${nodeApiBaseUrl}/workspaces/${request.workspaceId}/my-node-info?lessonProgressId=${request.lessonProgressId}`;
    return apiInstance.get(url).then((response) => {
        return getApiResponseData(response);
    });
};

export const getLatestNodesConfiguration = (request: IGetLatestNodesConfigurationRequest & {workspaceId: string}): Promise<IApiResponse<string>> => {
    return apiInstance.get(`${nodeApiBaseUrl}/${request.lessonId}/workspaces/${request.workspaceId}/nodes-configuration`).then((response) => {
        return getApiResponseData(response);
    });
};

export const updateLessonNodes = (request: IUpdateLessonNodesRequest & {workspaceId: string}): Promise<IApiResponse<any>> => {
    return apiInstance.put(`${nodeApiBaseUrl}/lessons/${request.lessonId}/workspaces/${request.workspaceId}/update-nodes`, request).then((response) => {
        return getApiResponseData(response);
    });
};

export const addNodeFile = (request: IAddNodeFileRequest & {workspaceId: string}): Promise<IApiResponse<FileFlat>> => {
    const pathMap: Record<NodeFileType, string> = {
        [NodeFileType.Video]: 'videos',
        [NodeFileType.Image]: 'images',
        [NodeFileType.Audio]: 'audios',
        [NodeFileType.InfoIcon]: 'info-icons'
    };

    const path = pathMap[request.nodeFileType];
    return apiInstance.post(`${nodeApiBaseUrl}/workspaces/${request.workspaceId}/${path}`,
        request.formData,
        {
            onUploadProgress: progressEvent => request.onUploadProgress(progressEvent),
            headers: {
                'Content-Type': 'multipart/form-data',
                'Content-Disposition': `form-data; name="file"; filename="${encodeURI(request.fileName)}"`
            }
        }
    ).then((response) => {
        return getApiResponseData(response);
    }).catch((error) => {
        return ({
            success: false,
            data: undefined,
            status: error.response?.status || 500,
            message: error.response?.statusText || "An error occurred while uploading node file",
            errors: error.response?.data?.errors || [{ code: "UnknownError", message: "Unexpected error" }],
        })
    });
};

export const getNodeFileList = (request: IGetNodeFileListRequest & {workspaceId: string}): Promise<IApiResponse<FileFlat[]>> => {
    return apiInstance.get(`${nodeApiBaseUrl}/workspaces/${request.workspaceId}/node-files`, {params: {nodeFileType: request.nodeFileType}}).then((response) => {
        return getApiResponseData(response);
    });
};

export const getNodeFileGroups = (request: IGetNodeFileGroupsRequest & {workspaceId: string}): Promise<IApiResponse<string[]>> => {
    return apiInstance.get(`${nodeApiBaseUrl}/workspaces/${request.workspaceId}/node-files/groups`, {params: {nodeFileType: request.nodeFileType}}).then((response) => {
        return getApiResponseData(response);
    });
};

export const deleteNodeFile = (request: IDeleteNodeFileRequest & {workspaceId: string}): Promise<IApiResponse<any>> => {
    return apiInstance.delete(`${nodeApiBaseUrl}/workspaces/${request.workspaceId}/node-files/${request.fileId}`).then((response) => {
        return getApiResponseData(response);
    });
};